import { useCallback, useState } from 'react';
import { History } from 'lucide-react';
import { auditReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, Pager, Dash, errMessage, DEFAULT_PAGE_SIZE } from './ReportCentreKit';
import type { JournalChangesFilter, JournalChangesReportDto, JournalChangeRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'JOURNAL_CHANGES';

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Source',  header: 'Source',     width: 12 },
  { key: 'Journal', header: 'Journal No', width: 18 },
  { key: 'Action',  header: 'Action',     width: 16 },
  { key: 'Detail',  header: 'Detail',     width: 50 },
  { key: 'By',      header: 'By',         width: 22 },
  { key: 'At',      header: 'At',         width: 20 },
];

function buildExportRows(rows: JournalChangeRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Source:  r.source,
    Journal: r.journalNumber ?? '',
    Action:  r.action,
    Detail:  r.detail ?? '',
    By:      r.byName ?? '',
    At:      r.at,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const JournalChangesPage = () => {
  const { hasPermission } = usePermission();

  const [filter, setFilter] = useState<JournalChangesFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [applied, setApplied] = useState<JournalChangesFilter | null>(null);
  const [data, setData] = useState<JournalChangesReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (base: JournalChangesFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setLoading(true);
    setError(null);
    try {
      const res = await auditReportApi.getJournalChanges(query);
      setData(res.data?.data ?? null);
      setApplied(query);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const run = useCallback(() => { void fetchPage(filter, 1); }, [fetchPage, filter]);

  if (!hasPermission(Permissions.RPT_AUDIT_TRAIL)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const page = data?.rows;
  const items = page?.items ?? [];

  const handleExcel = () => {
    if (!data || !page) return;
    exportToExcel({
      sheetName: 'Journal Changes',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(items),
      title: 'Journal Change Timeline',
      subtitle: `${data.fromDate} to ${data.toDate} · Page ${page.pageNumber} of ${page.totalPages}`,
    }, `JournalChanges_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  const handleCsv = () => {
    if (!data || !page) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(items), `JournalChanges_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <History size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Journal Change Timeline</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={items.length}
          filterSummary={`${filter.fromDate}..${filter.toDate}${filter.documentNumber ? ` doc=${filter.documentNumber}` : ''}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <label className={LABEL_CLS}>From Date *</label>
              <input type="date" value={filter.fromDate} onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
            </div>
            <div className="w-44">
              <label className={LABEL_CLS}>To Date *</label>
              <input type="date" value={filter.toDate} onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
            </div>
            <div className="w-52">
              <label className={LABEL_CLS}>Journal No (exact)</label>
              <input
                type="text"
                value={filter.documentNumber ?? ''}
                placeholder="e.g. JV-2026-000123"
                onChange={e => setFilter(f => ({ ...f, documentNumber: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
          </div>
          <RunReportButton onClick={run} isLoading={loading} disabled={!filter.fromDate || !filter.toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set the date range (optionally an exact journal number) and click "Run Report".' />
      )}

      {data && page && (
        <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Changes</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
            </span>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm border-collapse min-w-[1000px]">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                <tr>
                  <th className={cn(TH_CLS, 'w-28')}>Source</th>
                  <th className={cn(TH_CLS, 'w-40')}>Journal No</th>
                  <th className={cn(TH_CLS, 'w-32')}>Action</th>
                  <th className={TH_CLS}>Detail</th>
                  <th className={cn(TH_CLS, 'w-44')}>By</th>
                  <th className={cn(TH_CLS, 'w-40')}>At</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                      No journal changes match the selected criteria.
                    </td>
                  </tr>
                ) : items.map((r, i) => (
                  <tr key={`${r.journalId ?? 'x'}-${r.at}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2">
                      <Pill label={r.source} tone={r.source === 'WORKFLOW' ? 'purple' : 'blue'} />
                    </td>
                    <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {r.journalNumber ?? <Dash />}
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">{r.action}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.detail ?? <Dash />}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.byName ?? <Dash />}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(r.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            pageNumber={page.pageNumber}
            totalPages={page.totalPages}
            totalCount={page.totalCount}
            shownCount={items.length}
            loading={loading}
            onPage={p => { void fetchPage(applied ?? filter, p); }}
            noun="changes"
          />
        </div>
      )}
    </div>
  );
};
